import { shiftDevelopmentPercent } from '@sqi/formula-core';
import {
  emptyDevelopment,
  type DevelopmentContent,
  type DevelopmentDirection,
} from '@sqi/shared-schemas';
import type { OemWorkspace } from '../../lib/oem-client';
export { emptyDevelopment };
export const developmentCostLabels = {
  packaging: 'บรรจุภัณฑ์',
  labour: 'แรงงาน',
  tests: 'การทดสอบ',
  freight: 'ค่าขนส่ง',
  other: 'ค่าใช้จ่ายอื่น',
} as const;
type ChargeLabel = keyof typeof developmentCostLabels;
type Charge = DevelopmentContent['costs'][number];
export function chargeAmountFromInput(value: string): string | null {
  return value.trim() || null;
}
export function developmentCharge(content: DevelopmentContent, label: ChargeLabel): Charge {
  return (
    content.costs.find((c) => c.label === label) ?? {
      label,
      amountTHB: null,
      basis: 'per_batch',
      source: '',
      date: '',
      provenance: 'unknown',
    }
  );
}
export function updateDevelopmentCharge(
  content: DevelopmentContent,
  label: ChargeLabel,
  patch: Partial<Omit<Charge, 'label'>>,
): DevelopmentContent {
  const next = { ...developmentCharge(content, label), ...patch, label };
  const index = content.costs.findIndex((c) => c.label === label);
  return {
    ...content,
    costs:
      index < 0 ? [...content.costs, next] : content.costs.map((c, i) => (i === index ? next : c)),
  };
}
export interface DirectionSelection {
  state: 'unselected' | 'selected' | 'conflict';
  selection: number | null;
  revisionId: string;
  revisionHash: string;
  decisionId: string | null;
  decisionBasisHash: string | null;
  directionHash: string | null;
  createdAt: string | null;
}
export function customerDirectionIndex(
  evidence: DirectionSelection | null | undefined,
  revision: { id: string; hash: string },
  count: number,
): number | null {
  if (
    evidence?.state !== 'selected' ||
    evidence.revisionId !== revision.id ||
    evidence.revisionHash !== revision.hash ||
    evidence.decisionBasisHash !== revision.hash ||
    !evidence.decisionId ||
    !evidence.directionHash ||
    !Number.isInteger(evidence.selection) ||
    evidence.selection === null ||
    evidence.selection < 0 ||
    evidence.selection >= count
  )
    return null;
  return evidence.selection;
}
export function hasThreeComparedSummaries(c: {
  directions: readonly { label?: unknown; rationale?: unknown }[];
}): boolean {
  return (
    c.directions.length === 3 &&
    new Set(
      c.directions.map((d) => (typeof d.label === 'string' ? d.label.trim().toLowerCase() : '')),
    ).size === 3 &&
    c.directions.every(
      (d) =>
        typeof d.label === 'string' &&
        d.label.trim().length > 0 &&
        typeof d.rationale === 'string' &&
        d.rationale.trim().length > 0,
    )
  );
}
export function hasThreeCompleteDirections(c: Pick<DevelopmentContent, 'directions'>): boolean {
  return (
    c.directions.length === 3 &&
    new Set(c.directions.map((d) => d.label.trim().toLowerCase())).size === 3 &&
    c.directions.every((d) =>
      [d.label, d.rationale, d.risks, d.revalidation].every(
        (v) => typeof v === 'string' && v.trim().length > 0,
      ),
    )
  );
}
export function developmentBlockers(
  c: DevelopmentContent,
  decisions: readonly string[] = [],
  revision?: { id: string; hash: string; directionSelection?: DirectionSelection },
  technical = true,
) {
  const selected = revision
    ? customerDirectionIndex(revision.directionSelection, revision, c.directions.length)
    : null;
  return [
    ...(!(technical ? hasThreeCompleteDirections(c) : hasThreeComparedSummaries(c))
      ? ['ต้องเปรียบเทียบ 3 แนวทางที่ครบถ้วนและแตกต่างกันก่อนแบ่งปันหรือทบทวน']
      : []),
    ...(!c.identityConfirmed ? ['ยังไม่ยืนยันตัวตนอ้างอิง'] : []),
    ...(!c.briefConfirmed ? ['ยังไม่ยืนยันบรีฟ'] : []),
    ...(!['moisturizer', 'body_wash', 'cosmetic_serum'].includes(c.intake.category)
      ? ['ต้องให้ผู้เชี่ยวชาญจำแนกประเภท']
      : []),
    ...(!c.photos.some((p) => ['back', 'label'].includes(p.side) && p.quality === 'readable')
      ? ['ขาดภาพด้านหลังหรือฉลากที่อ่านได้']
      : []),
    ...(c.photos.some((p) => p.quality !== 'readable') ? ['มีภาพที่ยังอ่านไม่ได้หรือรอตรวจ'] : []),
    ...(!c.candidates[c.selectedCandidate ?? -1] ||
    c.candidates[c.selectedCandidate ?? -1]?.evidence.some((e) => e.uncertainty !== 'supported')
      ? ['หลักฐานยังไม่ยืนยัน มีข้อขัดแย้ง หรือเปิดแหล่งไม่ได้']
      : []),
    ...(revision?.directionSelection?.state === 'conflict'
      ? ['การเลือกแนวทางมีข้อขัดแย้ง ต้องสร้างฉบับใหม่ให้ลูกค้าเลือก']
      : selected === null
        ? ['ยังไม่เลือกแนวทาง']
        : []),
    ...(!decisions.includes('review_rd') ? ['R&D checker ต้องทบทวนฉบับนี้'] : []),
    ...(!decisions.includes('review_compliance') ? ['QA ต้องทบทวนฉบับนี้ · แหล่งกฎ DEMO'] : []),
    ...(!c.sample.requestId ? ['ยังไม่มีคำขอตัวอย่าง'] : []),
    ...(!c.sample.sampleId ? ['ยังไม่มีตัวอย่างที่เตรียมตามระบบเดิม'] : []),
    ...(!decisions.includes('accept_sample') ? ['ลูกค้ายังไม่รับตัวอย่างฉบับตรงกัน'] : []),
    'ไม่ใช่การอนุญาตให้ผลิตจริง',
  ];
}
const line = (
  inci: string,
  percent: string,
  fn: string,
  phase: 'A' | 'B' | 'C' = 'A',
  activeFraction = '1',
) => ({
  inci,
  percent,
  function: fn,
  phase,
  activeFraction,
  grade: 'ต้องยืนยันเกรดและสเปกจากผู้ขายก่อนทดลอง',
  supplierPricePerKg: null,
  priceSource: '',
  priceClass: 'unknown' as const,
  priceDate: '',
});
export function draftDirections(
  c: DevelopmentContent,
  workspace: OemWorkspace,
  qualified?: DevelopmentDirection,
): DevelopmentDirection[] {
  if (
    !c.identityConfirmed ||
    !c.briefConfirmed ||
    !['moisturizer', 'body_wash', 'cosmetic_serum'].includes(c.intake.category)
  )
    throw Error('ต้องยืนยันบรีฟ ตัวตน และประเภทเครื่องสำอางก่อน');
  if (qualified) {
    if (
      qualified.origin !== 'approved_base' ||
      !qualified.libraryId ||
      !qualified.libraryHash ||
      !qualified.libraryVersion
    )
      throw Error('ต้องใช้ snapshot ที่ server คัดคุณสมบัติแล้ว');
    const base = structuredClone(qualified);
    base.label = 'ใกล้เคียงคุณลักษณะที่ต้องการ · ฐานที่คัดคุณสมบัติ';
    base.rationale += ' · ทดลองเทียบคุณลักษณะกับบรีฟ ราคาจริงยังไม่ทราบ';
    const water = base.lines.findIndex((l) => l.inci === 'Aqua');
    const donor = base.lines.findIndex((l) => l.inci !== 'Aqua' && Number(l.percent) > 1);
    const results = [base];
    if (water >= 0 && donor >= 0 && Number(base.lines[water]?.percent) > 1) {
      for (const [delta, label, reason] of [
        [
          '1',
          'แตกต่าง / premium trial',
          'เพิ่ม as-supplied ของสารเดิม1% แล้วลดน้ำ1%; ต้องทดสอบความเข้ากันและผลต่อสัมผัส',
        ],
        [
          '-1',
          'lower-cost trial',
          'ลด as-supplied ของสารเดิม1% แล้วเพิ่มน้ำ1%; ขอราคาและเทียบ sensory ก่อนอ้างต้นทุนลด',
        ],
      ]) {
        const d = structuredClone(base);
        d.origin = 'modified_base';
        d.label = label ?? '';
        d.lines = d.lines.map((l, i) =>
          i === donor
            ? { ...l, percent: shiftDevelopmentPercent(l.percent, delta ?? '0') }
            : i === water
              ? { ...l, percent: shiftDevelopmentPercent(l.percent, delta === '1' ? '-1' : '1') }
              : l,
        );
        d.rationale = (reason ?? '') + ' · ราคาจริงยังไม่ทราบ ไม่รับรอง benefit';
        d.revalidation +=
          ' · composition changed: new R&D/QA/stability/preservation/sensory required';
        results.push(d);
      }
    }
    if (results.length < 3) {
      for (const [label, rationale] of [
        [
          'เน้นคุณลักษณะอ้างอิง',
          'คงองค์ประกอบฐานไว้และออกแบบการทดลองเทียบสัมผัสกับข้อมูลอ้างอิงที่ยืนยันแล้ว',
        ],
        [
          'เน้นกระบวนการและบรรจุภัณฑ์',
          'คงองค์ประกอบฐานไว้และประเมินกระบวนการกับความเข้ากันของบรรจุภัณฑ์ก่อนเสนอการเปลี่ยนสูตร',
        ],
      ]) {
        const option = structuredClone(base);
        option.label = label ?? '';
        option.rationale =
          (rationale ?? '') + ' · ยังไม่มีข้อมูลพอสำหรับเปลี่ยนเปอร์เซ็นต์หรืออ้างต้นทุน';
        option.revalidation += ' · ต้องให้ผู้ทบทวนอิสระประเมินแผนทดลองนี้';
        results.push(option);
      }
    }
    return results;
  }
  // No qualified source available: explicitly experimental templates.
  void workspace;
  const starter =
    c.intake.category === 'body_wash'
      ? [
          line('Aqua', '76.9', 'ตัวทำละลาย'),
          line('Sodium Laureth Sulfate', '12', 'สารทำความสะอาดแบบ as supplied', 'A', '0.28'),
          line('Cocamidopropyl Betaine', '8', 'สารทำความสะอาดร่วม', 'A', '0.3'),
          line('Glycerin', '2', 'สารให้ความชุ่มชื้น'),
          line(
            'Phenoxyethanol (and) Ethylhexylglycerin',
            '1',
            'ระบบกันเสียที่เสนอ · ต้องยืนยันสเปก',
            'C',
            '1',
          ),
          line('Citric Acid', '0.1', 'ตัวอย่างปริมาณสำหรับปรับ pH ต้องวัดจริง', 'C'),
        ]
      : c.intake.category === 'cosmetic_serum'
        ? [
            line('Aqua', '92.9', 'ตัวทำละลาย'),
            line('Glycerin', '3', 'สารให้ความชุ่มชื้น'),
            line('Niacinamide', '2', 'สารที่เสนอสำหรับเครื่องสำอาง', 'C'),
            line('Panthenol', '1', 'สารให้ความชุ่มชื้น', 'C'),
            line('Hydroxyethylcellulose', '0.1', 'ปรับเนื้อ'),
            line(
              'Phenoxyethanol (and) Ethylhexylglycerin',
              '1',
              'ระบบกันเสียที่เสนอ · ต้องยืนยันสเปก',
              'C',
            ),
          ]
        : [
            line('Aqua', '79', 'ตัวทำละลาย'),
            line('Glycerin', '4', 'สารให้ความชุ่มชื้น'),
            line('Caprylic/Capric Triglyceride', '10', 'สารให้สัมผัสนุ่ม', 'B'),
            line(
              'Glyceryl Stearate (and) PEG-100 Stearate',
              '3',
              'อิมัลซิไฟเออร์แบบผสม · ต้องยืนยันสเปก',
              'B',
            ),
            line('Cetearyl Alcohol', '3', 'ปรับโครงสร้าง', 'B'),
            line(
              'Phenoxyethanol (and) Ethylhexylglycerin',
              '1',
              'ระบบกันเสียที่เสนอ · ต้องยืนยันสเปก',
              'C',
            ),
          ];
  const common = {
    origin: 'new_experimental' as const,
    libraryId: null,
    process:
      c.intake.category === 'moisturizer'
        ? 'A: กระจายสารในน้ำ; B: เตรียมเฟสน้ำมัน; รวม A/B ตามอุณหภูมิที่สเปกกำหนด; C: เติมหลังเย็น · ห้ามใช้ค่าที่ไม่ได้ตรวจจากเกรดจริง'
        : 'กระจายสารในน้ำทีละรายการ ใช้แรงกวนเหมาะกับเนื้อและฟอง; เติม C ตามช่วงอุณหภูมิของสเปก; วัด pH ก่อนปรับ',
    equipment:
      'เครื่องชั่งสอบเทียบ เครื่องกวน ถังเหมาะสม pH meter; emulsion ต้องประเมิน homogenizer',
    targets: 'pH/viscosity/สี/กลิ่น: รอ R&D ตั้งค่าและเกณฑ์ตามสเปกจริง',
    preservation:
      'ระบบกันเสียเป็นข้อเสนอ ต้องตรวจข้อจำกัดเกรด/ตลาดและ challenge test ห้ามอ้างผ่านจากสูตร',
    tests:
      'stability, freeze/thaw ตามความเหมาะสม, microbiology/challenge, packaging compatibility, pH/viscosity; เกณฑ์/เวลา/ค่าใช้จ่ายยังไม่ทราบ',
    compatibility: 'ตรวจประจุ pH อุณหภูมิ ความเข้ากันของสารผสมและบรรจุภัณฑ์จากสเปกจริง',
    substitutions: 'ทุกการเปลี่ยนเกรด/ผู้ขาย/สารผสมต้องตรวจ active fraction และสร้างฉบับใหม่',
    revalidation: 'R&D, QA, sample, sensory และ indicative cost ต้องทบทวนใหม่เมื่อสูตร/บรีฟเปลี่ยน',
    assumptions:
      'ร่างเริ่มต้นจากแม่แบบสาธิตของซอฟต์แวร์ · ไม่มีการพิสูจน์สูตรแบรนด์อ้างอิง · ไม่มี provider วิจัยสด · ต้องตรวจสเปกจริง',
    risks: 'ความคงตัว การกันเสีย การระคายเคืองและประสิทธิภาพยังไม่พิสูจน์; ห้ามผลิตขายจากร่างนี้',
    balancing:
      'as-supplied weight%; totals exactly 100; display ROUND_HALF_UP at 6 places; Aqua balances explicit edits',
  };
  const closer = starter.map((l) => ({ ...l }));
  let premium = starter.map((l) => ({ ...l }));
  let economical = starter.map((l) => ({ ...l }));
  let premiumTradeoff = '';
  let economicalTradeoff = '';
  if (c.intake.category === 'moisturizer') {
    premium = premium.map((l) =>
      l.inci === 'Aqua' ? { ...l, percent: shiftDevelopmentPercent(l.percent, '-1') } : l,
    );
    premium.push(line('Panthenol', '1', 'สารให้ความชุ่มชื้นเพิ่มเติมที่เสนอ ต้องทดสอบ', 'C'));
    economical = economical.map((l) =>
      l.inci === 'Caprylic/Capric Triglyceride'
        ? { ...line('Mineral Oil', l.percent, 'ทางเลือก emollient เพื่อเทียบราคาและสัมผัส', 'B') }
        : l,
    );
    premiumTradeoff =
      'เพิ่ม Panthenol 1% แล้วลด Aqua 1%; เปรียบเทียบสัมผัสและความเข้ากันของ active โดยไม่รับรองประสิทธิภาพ';
    economicalTradeoff =
      'เปลี่ยน emollient 10% เป็น Mineral Oil เกรดเครื่องสำอาง เพื่อขอราคาเทียบ; ความรู้สึกเคลือบผิวและความคงตัวอาจเปลี่ยน ต้องทดลองใหม่';
  } else if (c.intake.category === 'cosmetic_serum') {
    premium = premium.map((l) =>
      l.inci === 'Aqua' ? { ...l, percent: shiftDevelopmentPercent(l.percent, '-0.1') } : l,
    );
    premium.push(
      line(
        'Sodium Hyaluronate',
        '0.1',
        'ทดลองเนื้อสัมผัส/ความชุ่มชื้น ต้องยืนยันน้ำหนักโมเลกุลและเกรด',
        'C',
      ),
    );
    economical = economical
      .filter((l) => l.inci !== 'Panthenol')
      .map((l) =>
        l.inci === 'Aqua' ? { ...l, percent: shiftDevelopmentPercent(l.percent, '1') } : l,
      );
    premiumTradeoff =
      'เพิ่ม Sodium Hyaluronate 0.1% แล้วลด Aqua 0.1%; เกรดและน้ำหนักโมเลกุลกระทบการละลาย ความหนืดและต้นทุน';
    economicalTradeoff =
      'ตัด Panthenol 1% และเพิ่ม Aqua 1% เพื่อลดความซับซ้อนและเทียบค่าใช้จ่าย; ยังมี Niacinamide แต่ไม่อ้างประโยชน์เท่าแนวทางเดิม';
  } else {
    premium = premium.map((l) =>
      l.inci === 'Sodium Laureth Sulfate'
        ? line(
            'Sodium Lauroyl Sarcosinate',
            '12',
            'surfactant ทางเลือกเพื่อทดลองสัมผัส/ฟอง ต้องตรวจ pH และสเปก',
            'A',
            '0.30',
          )
        : l,
    );
    economical = economical.map((l) =>
      ['Sodium Laureth Sulfate', 'Cocamidopropyl Betaine'].includes(l.inci)
        ? { ...l, percent: shiftDevelopmentPercent(l.percent, '-2') }
        : l.inci === 'Aqua'
          ? { ...l, percent: shiftDevelopmentPercent(l.percent, '4') }
          : l,
    );
    premiumTradeoff =
      'เปลี่ยนชนิด primary surfactant 12% เป็น Sodium Lauroyl Sarcosinate แบบสารละลาย 30% สมมติ; active/fอง/pH และการกันเสียต้องประเมินใหม่ ไม่มีข้ออ้างว่าอ่อนโยนกว่า';
    economicalTradeoff =
      'ลด primary และ co-surfactant อย่างละ 2% แล้วเพิ่ม Aqua 4%; active cleansing ลดลง ฟองและสัมผัสอาจต่าง ต้องเทียบราคากับผลทดลอง';
  }
  return [
    {
      label: 'ใกล้ลักษณะที่ต้องการ · แม่แบบเริ่มต้นไม่เติมน้ำหอม',
      lines: closer,
      rationale: `นำแม่แบบมาเทียบเนื้อและกลิ่นตามบรีฟ ${c.intake.texture || 'ยังไม่ระบุ'}; ยังไม่มีหลักฐานว่าใกล้แบรนด์อ้างอิง ต้องทดสอบกับตัวอย่างจริง`,
    },
    {
      label: 'แตกต่าง / premium trial · ปรับองค์ประกอบเพื่อทดลอง',
      lines: premium,
      rationale: premiumTradeoff,
    },
    {
      label: 'lower-cost trial · ทางเลือกองค์ประกอบ/ความเข้มข้น',
      lines: economical,
      rationale: economicalTradeoff,
    },
  ].map((variant) => ({
    ...common,
    ...variant,
    rationale:
      variant.rationale +
      ' · ราคาจริงทุกแนวทางยังไม่ทราบ ไม่รับรองว่าถูกกว่าหรือเป็น premium benefit จนมีหลักฐานราคาและผลทดสอบ',
  }));
}
